import { useState } from 'react'
import {
  CalendarDays, ArrowRight, ChevronDown, AlertCircle, CheckCircle2,
  UserX, Send, ShieldCheck, Clock, RotateCcw
} from 'lucide-react'
import AnchorLink from './AnchorLink'

const SubSectionHeading = ({ children, id }) => (
  <div className="mb-5" id={id}>
    <div className="group flex items-center gap-2">
      <h3 className="wiki-heading text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
        {children}
      </h3>
      {id && <AnchorLink id={id} />}
    </div>
    <div className="mt-2 h-px bg-light-border-primary dark:bg-dark-border-primary" />
  </div>
)

const Mono = ({ children }) => (
  <span className="wiki-mono text-xs bg-light-bg-tertiary dark:bg-dark-bg-tertiary px-1.5 py-0.5 rounded border border-light-border-primary dark:border-dark-border-primary">
    {children}
  </span>
)

const chainSteps = [
  {
    icon: CalendarDays,
    label: 'Departure date',
    desc: 'A "Fired" or "Left" date is added',
    color: 'text-primary-500',
    bg: 'bg-primary-50 dark:bg-primary-900/20',
  },
  {
    icon: UserX,
    label: 'Status',
    desc: 'Talent moves to Fired / Left',
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-900/20',
  },
  {
    icon: Send,
    label: 'Posts',
    desc: 'Published posts are unpublished',
    color: 'text-orange-600 dark:text-orange-400',
    bg: 'bg-orange-50 dark:bg-orange-900/20',
  },
  {
    icon: ShieldCheck,
    label: 'Account',
    desc: 'Sign-in is switched off',
    color: 'text-indigo-600 dark:text-indigo-400',
    bg: 'bg-indigo-50 dark:bg-indigo-900/20',
  },
]

const editCases = [
  { action: 'Moved to today', result: 'Status changes immediately', tone: 'ok' },
  { action: 'Moved further into the future', result: 'Status is reverted', tone: 'warn' },
  { action: 'Type changed Fired → Left', result: 'Status becomes Left', tone: 'ok' },
  { action: 'Date deleted', result: 'Status is reverted', tone: 'warn' },
  { action: 'Two dates — Fired past, Left future', result: 'Stays Fired: the latest due date wins', tone: 'ok' },
]

const notDoing = [
  'Change the status before the date has arrived',
  'Revert anything a human changed by hand',
  'Re-publish posts after a revert',
  'Touch job applications — talents only',
  'Call auth when the talent has no linked account',
  'Fire for a brand-new talent created with a departure date already filled in',
]

const faqItems = [
  {
    id: 'offboarding-q1',
    q: 'I set the last working day two days from now — will the person be marked as dismissed straight away?',
    a: (
      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
        No. A future date changes nothing on the spot — the talent keeps working. An hourly job
        checks whether anyone's day has arrived and moves the status then. If you later move the
        date, the status follows: pull it to today and it applies immediately, push it further away
        and it rolls back.
      </p>
    ),
  },
  {
    id: 'offboarding-q2',
    q: 'Why does changing the status to Fired ask me for a Start Work date?',
    a: (
      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
        A departure date cannot exist without a <span className="font-medium text-light-text-primary dark:text-dark-text-primary">Start Work</span> date,
        and most active talents have none. If both are missing you are asked for both at once —
        otherwise you would be stuck: the departure date would be rejected, and the status cannot
        change without it.
      </p>
    ),
  },
  {
    id: 'offboarding-q3',
    q: 'The dismissal was cancelled. Will the posts come back?',
    a: (
      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
        The status rolls back, the posts do not. Re-publishing is a deliberate decision, so it stays
        with you — open the post and publish it again.
      </p>
    ),
  },
  {
    id: 'offboarding-q4',
    q: 'I set the status to Fired by hand and then deleted the date. Will the status roll back?',
    a: (
      <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
        <p>
          Yes. A manual move to Fired or Left cannot happen without a departure date, so the date is
          part of that same action — removing it or pushing it into the future undoes it.
        </p>
        <p>
          Any other manual status change is left alone. If you moved someone to Pending, Available or
          any status unrelated to a departure date, deleting a date changes nothing.
        </p>
      </div>
    ),
  },
  {
    id: 'offboarding-q5',
    q: 'Only Fired and Left close the posts?',
    a: (
      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
        No — any inactive status does: <span className="italic">Pending</span>, <span className="italic">Denied</span>,{' '}
        <span className="italic">Refused</span>, <span className="italic">Deactive</span> and the rest of the
        list. The trigger is the talent becoming inactive, not the specific status.
      </p>
    ),
  },
  {
    id: 'offboarding-q6',
    q: 'Can a dismissed employee still sign in?',
    a: (
      <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
        No. When the status change makes the talent inactive, the linked account is switched off.
        Moving them back to an active status switches it on again. A talent with no linked account
        is simply skipped.
      </p>
    ),
  },
]

const OffboardingGuide = () => {
  const [openFaq, setOpenFaq] = useState(null)

  return (
    <div className="space-y-10">
      {/* Overview */}
      <section>
        <SubSectionHeading id="offboarding-overview">How it works</SubSectionHeading>

        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-5">
          Offboarding runs off the records themselves, not off buttons. That is why it behaves the
          same wherever the change comes from — the talent profile, the full edit form, the important
          dates tab, or an integration. One chain, each link triggering the next:
        </p>

        <div className="flex flex-col sm:flex-row items-stretch gap-2 mb-5">
          {chainSteps.map((step, i) => (
            <div key={step.label} className="flex items-center gap-2 flex-1">
              <div className={`flex-1 rounded-lg border border-light-border-primary dark:border-dark-border-primary p-3 ${step.bg}`}>
                <step.icon className={`h-5 w-5 mb-2 ${step.color}`} />
                <div className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                  {step.label}
                </div>
                <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-0.5">
                  {step.desc}
                </div>
              </div>
              {i < chainSteps.length - 1 && (
                <ArrowRight className="h-4 w-4 text-light-text-tertiary dark:text-dark-text-tertiary shrink-0 hidden sm:block" />
              )}
            </div>
          ))}
        </div>

        <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <CheckCircle2 className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
          <p className="text-xs text-blue-700 dark:text-blue-400">
            Everything after the status change is best-effort: if closing the posts or switching off
            the account fails, the status change still stands.
          </p>
        </div>
      </section>

      {/* Adding a date */}
      <section>
        <SubSectionHeading id="offboarding-add-date">Adding a departure date</SubSectionHeading>

        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <div className="rounded-lg border border-light-border-primary dark:border-dark-border-primary p-4">
            <div className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-1">
              Today or in the past
            </div>
            <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
              The status changes right away — the talent becomes Fired or Left.
            </p>
          </div>
          <div className="rounded-lg border border-light-border-primary dark:border-dark-border-primary p-4">
            <div className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-1">
              In the future
            </div>
            <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
              Nothing happens yet. The person keeps working until that day comes.
            </p>
          </div>
        </div>

        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
          The change is recorded in the talent's history exactly like a manual one, so statistics are
          unaffected. Automatic transitions carry a marker that tells them apart from human decisions —
          that marker is what makes a safe rollback possible.
        </p>
      </section>

      {/* Scheduled */}
      <section>
        <SubSectionHeading id="offboarding-scheduled">Dates set in advance</SubSectionHeading>

        <div className="flex items-start gap-3 p-4 rounded-lg border border-light-border-primary dark:border-dark-border-primary mb-4">
          <Clock className="h-5 w-5 text-primary-500 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
              Checked every hour, at minute 5 (Kyiv time)
            </div>
            <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-1">
              The job looks for departures whose day has arrived and applies the status then.
            </p>
          </div>
        </div>

        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
          It only picks up dates that were genuinely set ahead of time, and skips anyone whose status
          was changed by hand after the date came due. Without that guard a person who was hired back
          would be pushed into Fired again every hour.
        </p>
      </section>

      {/* Editing */}
      <section>
        <SubSectionHeading id="offboarding-edit-date">Editing an existing date</SubSectionHeading>

        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-4">
          The status is reconciled against <span className="font-medium text-light-text-primary dark:text-dark-text-primary">all</span>{' '}
          departure dates the talent has — so adding, moving, retyping and deleting all behave
          consistently.
        </p>

        <div
          className="w-full min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-light-border-primary dark:border-dark-border-primary"
          role="region"
          aria-label="Offboarding date editing behavior"
          tabIndex={0}
        >
          <table className="min-w-[560px] w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                <th className="px-4 py-2 font-medium">What you do</th>
                <th className="px-4 py-2 font-medium">What happens</th>
              </tr>
            </thead>
            <tbody>
              {editCases.map((c) => (
                <tr key={c.action} className="border-t border-light-border-primary dark:border-dark-border-primary">
                  <td className="px-4 py-2 text-light-text-primary dark:text-dark-text-primary">{c.action}</td>
                  <td className={`px-4 py-2 ${c.tone === 'warn' ? 'text-amber-700 dark:text-amber-400' : 'text-light-text-secondary dark:text-dark-text-secondary'}`}>
                    {c.result}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg mt-4">
          <RotateCcw className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-medium text-amber-900 dark:text-amber-300 mb-1">
              A rollback only undoes automatic changes
            </div>
            <p className="text-xs text-amber-700 dark:text-amber-400">
              If you changed the status yourself after the automation had set it, nothing is touched.
              Posts are never re-published automatically.
            </p>
          </div>
        </div>
      </section>

      {/* Manual status change */}
      <section>
        <SubSectionHeading id="offboarding-status-change">Changing the status by hand</SubSectionHeading>

        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-4">
          Moving someone to Fired or Left now requires a date — the manager has to say when the person
          was dismissed or left. The modal asks for exactly the fields that are missing:
        </p>

        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <div className="rounded-lg border border-light-border-primary dark:border-dark-border-primary p-4">
            <div className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-1">
              Start Work date exists
            </div>
            <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
              One field — the departure date.
            </p>
          </div>
          <div className="rounded-lg border border-light-border-primary dark:border-dark-border-primary p-4">
            <div className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-1">
              No Start Work date
            </div>
            <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
              Both fields at once — otherwise the departure date would be rejected and you could not
              proceed.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 bg-light-bg-secondary dark:bg-dark-bg-secondary rounded-lg border border-light-border-primary dark:border-dark-border-primary">
          <AlertCircle className="h-5 w-5 text-light-text-tertiary dark:text-dark-text-tertiary mt-0.5 shrink-0" />
          <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
            Transition rules are checked first, dates second. If your role is not allowed to make the
            transition — <Mono>Available → Fired</Mono> needs the Finance role — you will see the role
            message rather than the date one.
          </p>
        </div>
      </section>

      {/* Effects */}
      <section>
        <SubSectionHeading id="offboarding-effects">What the status change sets off</SubSectionHeading>

        <div className="space-y-3">
          <div className="rounded-lg border border-light-border-primary dark:border-dark-border-primary p-4">
            <div className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-1">
              The talent is marked inactive
            </div>
            <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
              Recalculated from the status name against the list of inactive statuses. Happens on every
              path — manual, automatic or scheduled.
            </p>
          </div>

          <div className="rounded-lg border border-light-border-primary dark:border-dark-border-primary p-4">
            <div className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-1">
              Published posts are unpublished
            </div>
            <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
              Triggered by the talent becoming inactive — so any inactive status counts, not only Fired
              and Left. Posts are closed the regular way, with the usual history entry and notification.
            </p>
          </div>

          <div className="rounded-lg border border-light-border-primary dark:border-dark-border-primary p-4">
            <div className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-1">
              The account is switched off
            </div>
            <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">
              A dismissed employee can no longer sign in. Returning them to an active status switches
              the account back on. Talents without a linked account are skipped.
            </p>
          </div>
        </div>
      </section>

      {/* Limits */}
      <section>
        <SubSectionHeading id="offboarding-limits">What it deliberately does not do</SubSectionHeading>
        <ul className="space-y-2">
          {notDoing.map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm text-light-text-secondary dark:text-dark-text-secondary">
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-light-text-tertiary dark:bg-dark-text-tertiary shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </section>

      {/* FAQ */}
      <section>
        <SubSectionHeading id="offboarding-faq">Common Questions</SubSectionHeading>
        <div className="space-y-2">
          {faqItems.map((item) => (
            <div
              key={item.id}
              id={item.id}
              className="border border-light-border-primary dark:border-dark-border-primary rounded-lg overflow-hidden"
            >
              <button
                onClick={() => setOpenFaq(openFaq === item.id ? null : item.id)}
                className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors"
              >
                <span className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                  {item.q}
                </span>
                <ChevronDown
                  className={`h-4 w-4 text-light-text-tertiary dark:text-dark-text-tertiary shrink-0 transition-transform ${openFaq === item.id ? 'rotate-180' : ''}`}
                />
              </button>
              {openFaq === item.id && (
                <div className="px-4 pb-4">{item.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}

export default OffboardingGuide
